import React, { useState } from "react";
import { ChevronDown, ChevronUp, History } from "lucide-react";

import { dataBR } from "../lib/historico";
import { numero, reais } from "../lib/numeros";
import { emDuasMedidas, medidasDoIngrediente, quantidade, sigla } from "../lib/unidades";
import GraficoPrecos from "./GraficoPrecos";

function Dado({ rotulo, valor, detalhe }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-1.5 border-b last:border-0 text-sm">
      <span className="text-muted-foreground shrink-0">{rotulo}</span>
      <span className="text-right tabular-nums min-w-0">
        <b>{valor}</b>{detalhe && <span className="text-xs text-muted-foreground"> {detalhe}</span>}
      </span>
    </div>
  );
}

// Coluna da esquerda: o essencial das compras anteriores do ingrediente escolhido.
export default function HistoricoIngrediente({ ing, compras, resumo: r }) {
  const [todas, setTodas] = useState(false);
  const unit = ing?.unit;
  const un = sigla(unit);
  const maior = medidasDoIngrediente(ing).find((m) => m.fator > 1); // pacote (ou kg) pra mostrar o preço "cheio"
  const media2 = r && maior ? { valor: r.media * maior.fator, um: maior.um } : null;
  const costuma = r ? emDuasMedidas(ing, r.qtdMedia) : null;

  return (
    <section className="h-full flex flex-col rounded-xl border bg-card overflow-hidden" data-e2e="historico-ingrediente">
      <div className="shrink-0 px-4 py-3 border-b flex items-center gap-2">
        <History className="w-4 h-4 text-muted-foreground" />
        <h2 className="font-semibold">Histórico</h2>
      </div>
      {!ing ? (
        <div className="flex-1 overflow-y-auto px-4 py-10 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3"><History className="w-5 h-5 text-muted-foreground" /></div>
          <p className="text-sm font-medium">Escolha um ingrediente</p>
          <p className="text-xs text-muted-foreground mt-1">Aqui aparecem as últimas compras e a média de preço.</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div>
            <p className="font-semibold leading-tight">{ing.name}</p>
            <p className="text-xs text-muted-foreground tabular-nums">{quantidade(ing.stock_quantity, unit)} em estoque</p>
          </div>
          {!r ? (
            <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">Nenhuma compra anterior. Esta vai ser a primeira.</p>
          ) : (
            <>
              <div className="rounded-xl bg-primary/10 border border-primary/20 px-3 py-2.5" data-e2e="media-5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Média das últimas {r.contadas} compras</p>
                <p className="text-2xl font-bold tabular-nums leading-tight mt-0.5">
                  {reais(r.media)}<span className="text-sm font-medium text-muted-foreground">/{un}</span>
                </p>
                {media2 && <p className="text-sm text-muted-foreground tabular-nums">{reais(media2.valor)}/{media2.um}</p>}
                <p className="text-[11px] text-muted-foreground">É o preço que entra na ficha técnica.</p>
              </div>
              <div>
                <Dado rotulo="Última" valor={`${reais(r.ultima.unitario)}/${un}`} detalhe={dataBR(r.ultima.data).slice(0, 5)} />
                <Dado rotulo={`Faixa (${r.contadas})`} valor={r.menor === r.maior ? "sempre igual" : `${reais(r.menor)} a ${reais(r.maior)}`} />
                <Dado rotulo="Costuma comprar" valor={costuma.principal} detalhe={costuma.secundaria ? `· ${quantidade(r.qtdMedia, unit)}` : ""} />
              </div>
              <GraficoPrecos compras={compras} media={r.media} unit={unit} />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  {todas ? `Todas as compras (${compras.length})` : "Últimas compras"}
                </p>
                <ul className="divide-y" data-e2e="lista-compras-anteriores">
                  {(todas ? compras : compras.slice(0, 4)).map((c, n) => (
                    <li key={c.id || n} className="py-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium tabular-nums">{dataBR(c.data)}</span>
                        <span className="font-semibold tabular-nums">{reais(c.total)}</span>
                      </div>
                      <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{emDuasMedidas(ing, c.qtd).principal}{c.fornecedor ? ` · ${c.fornecedor}` : ""}</span>
                        <span className="tabular-nums shrink-0">{reais(c.unitario)}/{un}</span>
                      </div>
                    </li>
                  ))}
                </ul>
                {compras.length > 4 && (
                  <button type="button" onClick={() => setTodas(!todas)}
                    className="mt-1 w-full rounded-md py-1.5 text-xs font-medium text-primary hover:bg-primary/10 flex items-center justify-center gap-1">
                    {todas ? <><ChevronUp className="w-3 h-3" /> Ver menos</> : <><ChevronDown className="w-3 h-3" /> Ver todas ({numero(compras.length, 0)})</>}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
